/* ============================================================
   AM2T — cart-pack-upsell.js
   <cart-complete-pack> inside the cart drawer
   (snippets/cart-pack-upsell.liquid).
   - Size pills + add buttons for the missing pack categories
   - Hides categories already in the cart, and the whole block once
     the pack is complete.
   Events dispatched use source 'cart-complete-pack' so the pack engine
   still evaluates the gift bottle. Depends on global.js.
   ============================================================ */

(() => {
  'use strict';

  if (customElements.get('cart-complete-pack')) return;

  const AM2T = window.AM2T;
  const t = AM2T.t;

  class CartCompletePack extends HTMLElement {
    connectedCallback() {
      this.items = this.querySelectorAll('[data-pack-item]');
      this.onCartUpdate = this.onCartUpdate.bind(this);

      this.bindSizePills();
      this.bindAddButtons();

      document.addEventListener('cart:update', this.onCartUpdate);
      AM2T.cart.get().then((cart) => this.applyVisibility(cart)).catch(() => {});
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.onCartUpdate);
    }

    bindSizePills() {
      this.items.forEach((item) => {
        const pills = item.querySelectorAll('[data-size-btn]');
        const addBtn = item.querySelector('[data-add-btn]:not([data-variant-id])');
        if (!pills.length || !addBtn) return;

        pills.forEach((pill) => {
          if (pill.disabled) return;
          pill.addEventListener('click', () => {
            pills.forEach((p) => p.setAttribute('aria-checked', 'false'));
            pill.setAttribute('aria-checked', 'true');
            addBtn.disabled = false;
            addBtn.removeAttribute('aria-disabled');
            addBtn.dataset.variantId = pill.dataset.variantId;
          });
        });
      });
    }

    bindAddButtons() {
      this.items.forEach((item) => {
        const addBtn = item.querySelector('[data-add-btn]');
        if (!addBtn) return;
        addBtn.addEventListener('click', () => {
          if (addBtn.disabled || !addBtn.dataset.variantId) return;
          this.addToCart(addBtn.dataset.variantId, addBtn);
        });
      });
    }

    async addToCart(variantId, button) {
      const originalText = button.textContent;
      button.classList.add('is-loading');
      button.disabled = true;
      button.textContent = t('adding');

      try {
        const cart = await AM2T.cart.add([{ id: parseInt(variantId, 10), quantity: 1 }]);
        AM2T.publishCart(cart, 'cart-complete-pack');

        button.textContent = t('added');
        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('is-loading');
          // Not re-enabled: the item is hidden by applyVisibility once the
          // category is in the cart.
        }, 1200);
      } catch (err) {
        console.warn('[cart-pack-upsell] add failed:', err);
        button.classList.remove('is-loading');
        button.disabled = false;
        button.textContent = t('try_again');
        setTimeout(() => { button.textContent = originalText; }, 2000);
      }
    }

    onCartUpdate(e) {
      const cart = e.detail && e.detail.cart;
      if (cart) {
        this.applyVisibility(cart);
      } else {
        AM2T.cart.get(true).then((c) => this.applyVisibility(c)).catch(() => {});
      }
    }

    applyVisibility(cart) {
      if (!AM2T.packIds()) {
        this.hidden = true;
        return;
      }
      const state = AM2T.packState(cart);
      if (state.complete) {
        this.hidden = true;
        return;
      }
      // A category with no stock that isn't already in the cart means the
      // pack can't be completed: don't offer it.
      const soldOut = (AM2T.packEligibleIds && AM2T.packEligibleIds.soldOut) || [];
      const inCart = { top: state.hasTop, bottom: state.hasBottom, socks: state.hasSocks };
      if (soldOut.some((c) => !inCart[c])) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      this.items.forEach((item) => {
        const c = item.dataset.category;
        item.hidden = (c === 'top' && state.hasTop) || (c === 'bottom' && state.hasBottom) || (c === 'socks' && state.hasSocks);
      });
    }
  }

  customElements.define('cart-complete-pack', CartCompletePack);
})();
